import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import type { ProviderEditorFormData } from '../schemas';

type ContactDetailsSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
  /** Embedded intake form URL (Feature 103 pilot). Read-only: set only via
   *  manual SQL backfill, never through this form. */
  intakeFormUrl?: string | null;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * ContactDetailsSection - Provider contact information and online presence
 * 
 * Contains:
 * - Phone number
 * - Website URL
 * - Calendly scheduling URL
 * - Hours description
 * - Social media handles (JSON object)
 * - Google Place ID
 */
export function ContactDetailsSection({
  control,
  errors,
  intakeFormUrl,
}: ContactDetailsSectionProps) {
  return (
    <div className="space-y-6">
      {/* Phone Number */}
      <FormField
        label="Phone Number"
        htmlFor="phone-number"
        error={errors.provider?.phoneNumber?.message}
        hint="Primary contact number"
      >
        <Controller
          name="provider.phoneNumber"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="phone-number"
              type="tel"
              maxLength={50}
              className={errors.provider?.phoneNumber ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Website URL */}
      <FormField
        label="Website URL"
        htmlFor="website-url"
        error={errors.provider?.websiteUrl?.message}
        hint="Company website address"
      >
        <Controller
          name="provider.websiteUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="website-url"
              type="url"
              className={errors.provider?.websiteUrl ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Calendly URL */}
      <FormField
        label="Calendly Scheduling URL"
        htmlFor="calendly-url"
        error={errors.provider?.calendlyUrl?.message}
        hint="Link for clients to schedule appointments"
      >
        <Controller
          name="provider.calendlyUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="calendly-url"
              type="url"
              className={errors.provider?.calendlyUrl ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Embedded Intake Form URL (Feature 103, The Key pilot) — read-only,
          set only via manual SQL backfill. */}
      {intakeFormUrl && (
        <FormField
          label="Embedded Intake Form URL"
          htmlFor="intake-form-url"
          hint="Read-only: set via manual backfill, not editable here"
        >
          <Input
            id="intake-form-url"
            value={intakeFormUrl}
            disabled
            className="bg-gray-100 text-gray-600"
          />
        </FormField>
      )}

      {/* Hours Description */}
      <FormField
        label="Hours Description"
        htmlFor="hours-description"
        error={errors.provider?.hoursDescription?.message}
        hint="Operating hours (max 255 characters)"
      >
        <Controller
          name="provider.hoursDescription"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="hours-description"
              maxLength={255}
              className={errors.provider?.hoursDescription ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Social Media Handles */}
      <FormField
        label="Social Media Handles"
        htmlFor="social-handles"
        hint='JSON format: {"facebook": "username", "linkedin": "username"}'
      >
        <Controller
          name="provider.socialHandles"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={
                field.value
                  ? JSON.stringify(field.value, null, 2)
                  : ''
              }
              onChange={(e) => {
                try {
                  const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                  field.onChange(parsed);
                } catch {
                  // Keep the raw value if it's not valid JSON yet
                  field.onChange(e.target.value);
                }
              }}
              id="social-handles"
              rows={4}
              className={`font-mono text-sm ${
                errors.provider?.socialHandles ? 'border-red-500' : ''
              }`}
            />
          )}
        />
      </FormField>

      {/* Google Place ID */}
      <FormField
        label="Google Place ID"
        htmlFor="place-id"
        error={errors.provider?.placeId?.message}
        hint="For Google Business integration and reviews"
      >
        <Controller
          name="provider.placeId"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="place-id"
              className={`font-mono ${
                errors.provider?.placeId ? 'border-red-500' : ''
              }`}
            />
          )}
        />
      </FormField>
    </div>
  );
}

